/**
 * Общие компоненты сайта JustKiwi: личного кабинета, публичного профиля и вики.
 *
 * Vanilla JS, ES-модуль (собирается esbuild вместе со страницей). Здесь: HTTP-клиент,
 * DOM-помощники, иконки, кнопки и поля, уведомления, окна на нативном <dialog>,
 * форматирование дат и чисел, шапка сайта. Стили — классы Tailwind и компоненты из css/site.css.
 *
 * Анимации — Motion mini (js/vendor/motion.js). При «меньше движения» в системе
 * анимации не запускаются: элементы сразу стоят в конечном состоянии.
 */
import { animate } from "./vendor/motion.js";

// ──────────────────────────── HTTP-клиент ────────────────────────────

/** Запросы к API сайта с cookie-сессией. Ответ всегда { ok, status, data, error }. */
export const api = {
  async req(method, url, body) {
    const options = { method, credentials: "include", headers: {} };
    if (body !== undefined) {
      options.headers["Content-Type"] = "application/json";
      options.body = JSON.stringify(body);
    }
    let response;
    try {
      response = await fetch(url, options);
    } catch (networkError) {
      return { ok: false, status: 0, data: {}, error: "нет связи с сервером" };
    }
    const data = await response.json().catch(() => ({}));
    return { ok: response.ok, status: response.status, data, error: data && data.error };
  },
  get(url) { return this.req("GET", url); },
  post(url, body) { return this.req("POST", url, body ?? {}); },
  put(url, body) { return this.req("PUT", url, body ?? {}); },
  patch(url, body) { return this.req("PATCH", url, body ?? {}); },
  del(url) { return this.req("DELETE", url); },
};

// ──────────────────────────── DOM ────────────────────────────

const SVG_NS = "http://www.w3.org/2000/svg";

/**
 * Создать HTML-элемент. attrs: class, style, html (innerHTML — только для своей
 * статичной разметки), on<Событие> (обработчик), остальное — атрибуты
 * (false/null/undefined пропускаются). children: узел, строка, число или массив.
 */
export function el(tag, attrs, children) {
  return fill(document.createElement(tag), attrs, children);
}

/** То же для SVG-элементов (графики, иконки). */
export function svg(tag, attrs, children) {
  return fill(document.createElementNS(SVG_NS, tag), attrs, children);
}

function fill(node, attrs, children) {
  if (attrs) {
    for (const [key, value] of Object.entries(attrs)) {
      if (value === false || value === null || value === undefined) continue;
      if (key === "class") node.setAttribute("class", value);
      else if (key === "html") node.innerHTML = value;
      else if (key.startsWith("on") && typeof value === "function") node.addEventListener(key.slice(2).toLowerCase(), value);
      else node.setAttribute(key, value === true ? "" : value);
    }
  }
  appendChildren(node, children);
  return node;
}

/** Добавить детей: узлы, строки и числа (как текст), вложенные массивы; null пропускается. */
export function appendChildren(node, children) {
  if (children === null || children === undefined || children === false) return;
  if (Array.isArray(children)) {
    for (const child of children) appendChildren(node, child);
  } else if (children instanceof Node) {
    node.appendChild(children);
  } else {
    node.appendChild(document.createTextNode(String(children)));
  }
}

// ──────────────────────────── Иконки ────────────────────────────

/** Контуры иконок Lucide (ISC License, lucide.dev), 24×24, обводка currentColor. */
const ICONS = {
  user: '<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
  "chart-column": '<path d="M3 3v16a2 2 0 0 0 2 2h16"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/>',
  users: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><path d="M16 3.128a4 4 0 0 1 0 7.744"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><circle cx="9" cy="7" r="4"/>',
  wallet: '<path d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1"/><path d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4"/>',
  crown: '<path d="M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z"/><path d="M5 21h14"/>',
  receipt: '<path d="M12 17V7"/><path d="M16 8h-6a2 2 0 0 0 0 4h4a2 2 0 0 1 0 4H8"/><path d="M4 3a1 1 0 0 1 1-1 1.3 1.3 0 0 1 .7.2l.933.6a1.3 1.3 0 0 0 1.4 0l.934-.6a1.3 1.3 0 0 1 1.4 0l.933.6a1.3 1.3 0 0 0 1.4 0l.933-.6a1.3 1.3 0 0 1 1.4 0l.934.6a1.3 1.3 0 0 0 1.4 0l.933-.6A1.3 1.3 0 0 1 19 2a1 1 0 0 1 1 1v18a1 1 0 0 1-1 1 1.3 1.3 0 0 1-.7-.2l-.933-.6a1.3 1.3 0 0 0-1.4 0l-.934.6a1.3 1.3 0 0 1-1.4 0l-.933-.6a1.3 1.3 0 0 0-1.4 0l-.933.6a1.3 1.3 0 0 1-1.4 0l-.934-.6a1.3 1.3 0 0 0-1.4 0l-.933.6a1.3 1.3 0 0 1-.7.2 1 1 0 0 1-1-1z"/>',
  sparkles: '<path d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z"/><path d="M20 2v4"/><path d="M22 4h-4"/><circle cx="4" cy="20" r="2"/>',
  shirt: '<path d="M20.38 3.46 16 2a4 4 0 0 1-8 0L3.62 3.46a2 2 0 0 0-1.34 2.23l.58 3.47a1 1 0 0 0 .99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 0 0 2-2V10h2.15a1 1 0 0 0 .99-.84l.58-3.47a2 2 0 0 0-1.34-2.23z"/>',
  link: '<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>',
  "shield-check": '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/>',
  history: '<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l4 2"/>',
  "eye-off": '<path d="M10.733 5.076a10.744 10.744 0 0 1 11.205 6.575 1 1 0 0 1 0 .696 10.747 10.747 0 0 1-1.444 2.49"/><path d="M14.084 14.158a3 3 0 0 1-4.242-4.242"/><path d="M17.479 17.499a10.75 10.75 0 0 1-15.417-5.151 1 1 0 0 1 0-.696 10.75 10.75 0 0 1 4.446-5.143"/><path d="m2 2 20 20"/>',
  gavel: '<path d="m14 13-8.381 8.38a1 1 0 0 1-3.001-3l8.384-8.381"/><path d="m16 16 6-6"/><path d="m21.5 10.5-8-8"/><path d="m8 8 6-6"/><path d="m8.5 7.5 8 8"/>',
  "key-round": '<path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z"/><circle cx="16.5" cy="7.5" r=".5" fill="currentColor"/>',
  sun: '<circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/>',
  moon: '<path d="M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6 6 0 0 0 8.268 8.268c.344-.215.825-.004.803.401"/>',
  bell: '<path d="M10.268 21a2 2 0 0 0 3.464 0"/><path d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326"/>',
  "chevron-down": '<path d="m6 9 6 6 6-6"/>',
  copy: '<rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
  search: '<path d="m21 21-4.34-4.34"/><circle cx="11" cy="11" r="8"/>',
  "log-out": '<path d="m16 17 5-5-5-5"/><path d="M21 12H9"/><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>',
  x: '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
  "external-link": '<path d="M15 3h6v6"/><path d="M10 14 21 3"/><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>',
  upload: '<path d="M12 3v12"/><path d="m17 8-5-5-5 5"/><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>',
  menu: '<path d="M4 5h16"/><path d="M4 12h16"/><path d="M4 19h16"/>',
  share: '<circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" x2="15.42" y1="13.51" y2="17.49"/><line x1="15.41" x2="8.59" y1="6.51" y2="10.49"/>',
  lock: '<rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>',
  trophy: '<path d="M10 14.66V17a1 1 0 0 1-1 1 2 2 0 0 0-2 2v2"/><path d="M14 14.66V17a1 1 0 0 0 1 1 2 2 0 0 1 2 2v2"/><path d="M17.916 10H19.5A2.5 2.5 0 0 0 22 7.5V5a1 1 0 0 0-1-1h-3"/><path d="M4 22h16"/><path d="M6 9a6 6 0 0 0 12 0V3a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1z"/><path d="M6.084 10H4.5A2.5 2.5 0 0 1 2 7.5V5a1 1 0 0 1 1-1h3"/>',
  clock: '<circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>',
  calendar: '<path d="M8 2v3"/><path d="M16 2v3"/><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M8 13h.01"/><path d="M12 13h.01"/><path d="M16 13h.01"/><path d="M8 17h.01"/><path d="M12 17h.01"/><path d="M16 17h.01"/>',
  rotate: '<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>',
  globe: '<circle cx="12" cy="12" r="10"/><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/><path d="M2 12h20"/>',
};

/**
 * Добавить иконки в общий набор. Страницы со своими иконками (вики: редактор, выноски)
 * регистрируют их в своём модуле — так они не попадают в сборку кабинета и профиля.
 */
export function addIcons(map) {
  Object.assign(ICONS, map);
}

/** SVG-иконка по имени из ICONS. Декоративная (aria-hidden) — смысл несёт соседний текст. */
export function icon(name, size = 18, extraClass = "") {
  const node = svg("svg", {
    viewBox: "0 0 24 24",
    width: size,
    height: size,
    fill: "none",
    stroke: "currentColor",
    "stroke-width": 2,
    "stroke-linecap": "round",
    "stroke-linejoin": "round",
    "aria-hidden": "true",
    class: `shrink-0 ${extraClass}`,
  });
  node.innerHTML = ICONS[name] || "";
  return node;
}

// ──────────────────────────── Движение ────────────────────────────

/** Включено ли в системе «уменьшить движение». */
export function reducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Пружина для ответа на действия: окна, уведомления, подсветка раздела. */
export const SPRING = { type: "spring", visualDuration: 0.35, bounce: 0.15 };

/**
 * Анимировать элемент (Motion mini, Web Animations API). Ключевые кадры описывают
 * движение ИЗ начального состояния в естественное состояние элемента — поэтому при
 * «меньше движения» анимацию можно просто пропустить. Возвращает Promise окончания.
 */
export function play(node, keyframes, options = SPRING) {
  if (!node || reducedMotion()) return Promise.resolve();
  try {
    // Управление анимацией Motion — thenable: Promise.resolve дождётся её окончания.
    return Promise.resolve(animate(node, keyframes, options));
  } catch (error) {
    console.error(error);
    return Promise.resolve();
  }
}

/**
 * Досчитать число в узле от 0 до [to] (≈0.7 с, замедление к концу).
 * format — как печатать промежуточные значения (по умолчанию с разрядами).
 */
export function countUp(node, to, { duration = 0.7, delay = 0, format = fmtNumber } = {}) {
  const target = Number(to) || 0;
  if (reducedMotion() || target === 0) {
    node.textContent = format(target);
    return;
  }
  node.textContent = format(0);
  const startAt = performance.now() + delay * 1000;
  const step = (now) => {
    const progress = Math.min(1, Math.max(0, (now - startAt) / (duration * 1000)));
    const eased = 1 - Math.pow(1 - progress, 3);
    node.textContent = format(Math.round(target * eased));
    if (progress < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

// ──────────────────────────── Очистка при уходе с экрана ────────────────────────────

/** Обработчики ухода, привязанные к элементам страницы: { node, fn }. */
const leaveHandlers = new Set();

/**
 * Выполнить fn, когда элемент node уйдёт со страницы (например, закрыть 3D-сцену
 * и освободить WebGL-контекст при смене раздела кабинета).
 */
export function onLeave(node, fn) {
  leaveHandlers.add({ node, fn });
}

/**
 * Уход с экрана. С container — закрыть всё, что лежит внутри него (прежнее
 * содержимое перед заменой). Без аргумента — закрыть то, чего уже нет на странице
 * (разделы, которые построились, но так и не были показаны).
 */
export function leaveView(container) {
  for (const entry of [...leaveHandlers]) {
    const gone = container ? container.contains(entry.node) : !entry.node.isConnected;
    if (!gone) continue;
    leaveHandlers.delete(entry);
    try { entry.fn(); } catch (error) { console.error(error); }
  }
}

// ──────────────────────────── Кнопки, поля, блоки ────────────────────────────

/**
 * Показывать спиннер в кнопке, пока идёт task. Кнопка на это время отключена,
 * а её ширина зафиксирована, чтобы вёрстка не прыгала.
 */
export async function busy(button, task) {
  const saved = [...button.childNodes];
  button.style.minWidth = button.offsetWidth + "px";
  button.disabled = true;
  button.replaceChildren(el("span", { class: "jk-spinner", "aria-hidden": "true" }));
  try {
    return await task();
  } finally {
    button.disabled = false;
    button.replaceChildren(...saved);
    button.style.minWidth = "";
  }
}

/**
 * Кнопка. variant: default | primary | danger | ghost | ghost-danger.
 * Если onClick вернул Promise — кнопка показывает спиннер до его завершения.
 */
export function btn(text, onClick, variant = "default", { iconName, size, type = "button", title } = {}) {
  const button = el("button", {
    type,
    title,
    class: `jk-btn jk-btn--${variant}${size === "sm" ? " jk-btn--sm" : ""}${!text ? " jk-btn--icon" : ""}`,
    "aria-label": !text ? title : null,
  }, [iconName ? icon(iconName, size === "sm" ? 15 : 17) : null, text || null]);
  if (onClick) {
    button.addEventListener("click", (event) => {
      const result = onClick(event);
      if (result && typeof result.then === "function") busy(button, () => result);
    });
  }
  return button;
}

/** Поле ввода с единым стилем. */
export function input(attrs = {}) {
  return el("input", { type: "text", ...attrs, class: `jk-input ${attrs.class || ""}` });
}

/** Контейнер-карточка: поверхность с тонкой рамкой. */
export function card(children, extra = "") {
  return el("div", { class: `rounded-2xl border border-line bg-surface p-5 ${extra}` }, children);
}

/** Строка «подпись — значение». */
export function field(label, value) {
  return el("div", { class: "flex items-baseline justify-between gap-4 py-2.5 border-b border-line last:border-0 text-sm" }, [
    el("span", { class: "text-muted" }, label),
    el("span", { class: "font-medium text-right break-words min-w-0" }, value ?? "—"),
  ]);
}

/**
 * Заголовок экрана: крупное название, пояснение и (необязательно) действия справа.
 * Своих внешних отступов нет — расстояние до содержимого задаёт контейнер экрана.
 */
export function pageTitle(title, description, actions) {
  return el("header", { class: "flex flex-wrap items-end justify-between gap-3" }, [
    el("div", { class: "min-w-0" }, [
      el("h1", { class: "font-display text-2xl sm:text-[28px] leading-tight" }, title),
      description ? el("p", { class: "mt-2 text-sm text-muted max-w-prose" }, description) : null,
    ]),
    actions ? el("div", { class: "flex flex-wrap gap-2" }, actions) : null,
  ]);
}

/** Заголовок блока внутри экрана. */
export function blockTitle(text, aside) {
  return el("div", { class: "mb-3 flex items-baseline justify-between gap-3" }, [
    el("h2", { class: "text-base font-semibold" }, text),
    aside || null,
  ]);
}

/** Пустое состояние: что здесь появится и как этого добиться. */
export function emptyState(text, hint) {
  return el("div", { class: "rounded-2xl border border-dashed border-line px-5 py-10 text-center" }, [
    el("p", { class: "text-sm" }, text),
    hint ? el("p", { class: "mt-1 text-sm text-muted" }, hint) : null,
  ]);
}

/** Прямоугольник-заглушка загрузки. */
export function skeleton(classes) {
  return el("div", { class: `jk-skeleton ${classes}`, "aria-hidden": "true" });
}

/** Заглушка экрана: заголовок и несколько строк — пока идут запросы. */
export function skeletonPage() {
  return el("div", { "aria-busy": "true", "aria-label": "Загрузка" }, [
    skeleton("h-8 w-56 mb-3"),
    skeleton("h-4 w-80 max-w-full mb-8"),
    skeleton("h-24 w-full mb-3"),
    skeleton("h-24 w-full mb-3"),
    skeleton("h-24 w-full"),
  ]);
}

/** Тег главной группы игрока в её игровом цвете. Для базовой группы тега нет. */
export function tagBadge(tag, color) {
  if (!tag) return null;
  return el("span", { class: "jk-tag", style: `--tag:${color || "#AAAAAA"}` }, tag);
}

/** Подписи статусов присутствия (как в игровом /status). */
const STATUS = {
  ONLINE: { text: "В сети", color: "text-kiwi", live: true },
  AWAY: { text: "Отошёл", color: "text-gold" },
  BUSY: { text: "Занят", color: "text-danger" },
  APPEAR_OFFLINE: { text: "Невидимка — для других не в сети", color: "text-muted" },
};

/** Статус присутствия: точка + текст. null — «не в сети». */
export function statusBadge(status) {
  const info = STATUS[status] || { text: "Не в сети", color: "text-muted" };
  return el("span", { class: "inline-flex items-center gap-2 text-sm" }, [
    el("span", { class: `jk-dot ${info.color}${info.live ? " jk-dot--live" : ""}` }),
    el("span", { class: info.live ? "text-ink" : "text-muted" }, info.text),
  ]);
}

/**
 * Кнопка «Скопировать»: после копирования на секунду показывает галочку.
 * Пустой label — кнопка из одной иконки (подпись уходит в title и aria-label).
 */
export function copyButton(value, label = "Скопировать", size = "sm") {
  const button = btn(label, null, "default", { iconName: "copy", size, title: label || "Скопировать" });
  button.addEventListener("click", async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      toast("Не удалось скопировать — выделите текст вручную", "error");
      return;
    }
    const saved = [...button.childNodes];
    button.replaceChildren(icon("check", 15, "text-kiwi"), label ? "Скопировано" : "");
    setTimeout(() => button.replaceChildren(...saved), 1400);
  });
  return button;
}

// ──────────────────────────── Уведомления ────────────────────────────

/** Короткое уведомление внизу экрана. type: ok | error | info. */
export function toast(message, type = "ok") {
  const box = document.getElementById("toast");
  if (!box) return;
  const icons = { ok: ["check", "text-kiwi"], error: ["x", "text-danger"], info: ["bell", "text-muted"] };
  const [iconName, color] = icons[type] || icons.info;
  const item = el("div", {
    role: type === "error" ? "alert" : "status",
    class: "flex items-center gap-2.5 max-w-[calc(100vw-32px)] px-4 py-3 rounded-xl border border-line bg-surface text-sm shadow-soft",
  }, [icon(iconName, 17, color), el("span", null, message)]);
  box.appendChild(item);
  play(item, { opacity: [0, 1], transform: ["translateY(14px) scale(0.96)", "none"] });
  setTimeout(async () => {
    await play(item, { opacity: [1, 0], transform: ["none", "translateY(8px)"] }, { duration: 0.18 });
    item.remove();
  }, 3200);
}

/** Уведомление об ошибке запроса: что не получилось и что ответил сервер. */
export function apiError(prefix, response) {
  toast(`${prefix}: ${response.error || (response.status ? "ошибка " + response.status : "нет связи с сервером")}`, "error");
}

// ──────────────────────────── Окна ────────────────────────────

/**
 * Модальное окно на нативном <dialog>: фокус внутри, Esc закрывает, фон затемнён.
 * sheet — нижний лист на всю ширину (меню разделов на телефоне).
 * className — модификаторы окна (например, широкое окно вики); padded: false — без
 * внутренних отступов, когда содержимое размечает их само (палитра поиска).
 * Возвращает { node, close(value) }; onClose(value) вызывается после закрытия.
 */
export function openDialog({ title, body, actions, sheet = false, onClose, label, className = "", padded = true }) {
  const node = el("dialog", {
    class: `jk-dialog${sheet ? " jk-sheet" : ""}${className ? " " + className : ""}`,
    "aria-label": label || title || null,
  });
  const inner = el("div", { class: sheet ? "px-3 pt-2 pb-4" : (padded ? "p-6" : "") }, [
    sheet ? el("div", { class: "mx-auto mb-2 mt-1 h-1 w-10 rounded-full bg-line", "aria-hidden": "true" }) : null,
    title ? el("h2", { class: "font-display text-lg leading-snug mb-2" }, title) : null,
    body,
    actions && actions.length ? el("div", { class: "mt-6 flex flex-wrap justify-end gap-2" }, actions) : null,
  ]);
  node.appendChild(inner);

  let closing = false;
  async function close(value) {
    if (closing) return;
    closing = true;
    await play(node, sheet
      ? { transform: ["none", "translateY(100%)"] }
      : { opacity: [1, 0], transform: ["none", "scale(0.97)"] }, { duration: 0.16 });
    node.close(value ?? "");
  }
  // Клик по затемнённому фону попадает в сам <dialog> — закрываем.
  node.addEventListener("click", (event) => { if (event.target === node) close(); });
  node.addEventListener("close", () => {
    node.remove();
    if (onClose) onClose(node.returnValue);
  });

  document.body.appendChild(node);
  node.showModal();
  play(node, sheet
    ? { transform: ["translateY(100%)", "none"] }
    : { opacity: [0, 1], transform: ["scale(0.96)", "none"] });
  return { node, close };
}

/**
 * Подтверждение опасного действия: окно «Отмена» / [confirmText]. После подтверждения
 * action выполняется со спиннером на кнопке окна, и окно закрывается, когда действие
 * завершилось. Кнопка, которая открыла окно, при этом не блокируется.
 */
export function confirmAction({ title, message, confirmText = "Подтвердить", action, variant = "danger" }) {
  let dialog = null;
  const confirmButton = btn(confirmText, null, variant);
  confirmButton.addEventListener("click", () => busy(confirmButton, async () => {
    await action();
    dialog.close();
  }));
  dialog = openDialog({
    title,
    body: el("p", { class: "text-sm text-muted leading-relaxed" }, message),
    actions: [btn("Отмена", () => dialog.close()), confirmButton],
  });
}

// ──────────────────────────── Подсказка к графикам ────────────────────────────

let tooltipNode = null;

/** Показать подсказку над точкой (x, y) экрана: значение крупно, подпись под ним. */
export function showTooltip(x, y, value, caption) {
  if (!tooltipNode) {
    tooltipNode = el("div", { class: "jk-tooltip", role: "tooltip" });
    document.body.appendChild(tooltipNode);
  }
  tooltipNode.replaceChildren(el("strong", null, value), el("span", null, caption));
  tooltipNode.style.left = Math.round(x) + "px";
  tooltipNode.style.top = Math.round(y) + "px";
  tooltipNode.hidden = false;
  // Подсказка центрируется над точкой; у края окна (крайние столбцы и точки графика) сдвигаем её внутрь целиком.
  const box = tooltipNode.getBoundingClientRect();
  const margin = 8;
  const shift = box.left < margin ? margin - box.left : box.right > window.innerWidth - margin ? window.innerWidth - margin - box.right : 0;
  if (shift) tooltipNode.style.left = Math.round(x + shift) + "px";
}

export function hideTooltip() {
  if (tooltipNode) tooltipNode.hidden = true;
}

// ──────────────────────────── Форматирование ────────────────────────────

const MONTHS_GENITIVE = ["января", "февраля", "марта", "апреля", "мая", "июня",
  "июля", "августа", "сентября", "октября", "ноября", "декабря"];

/** Число с разрядами по-русски: 37 500. */
export function fmtNumber(value) {
  return new Intl.NumberFormat("ru-RU").format(Math.round(Number(value) || 0));
}

/** Склонение по числу: plural(5, "день", "дня", "дней") → «дней». */
export function plural(count, one, few, many) {
  const mod10 = Math.abs(count) % 10;
  const mod100 = Math.abs(count) % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

/** 12.03.2026, 14:30 */
export function fmtDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date)) return String(value);
  return date.toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** 12 марта 2026 */
export function fmtDateLong(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date)) return String(value);
  return `${date.getDate()} ${MONTHS_GENITIVE[date.getMonth()]} ${date.getFullYear()}`;
}

/** 12 марта (без года) */
export function fmtDayMonth(date) {
  return `${date.getDate()} ${MONTHS_GENITIVE[date.getMonth()]}`;
}

/** 14:30 */
export function fmtTime(value) {
  const date = new Date(value);
  if (isNaN(date)) return "";
  return date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
}

/** Длительность: «3 ч 20 мин», «45 мин», «меньше минуты». */
export function fmtDuration(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  if (total < 60) return total > 0 ? "меньше минуты" : "0 мин";
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (hours === 0) return `${minutes} мин`;
  return minutes > 0 ? `${fmtNumber(hours)} ч ${minutes} мин` : `${fmtNumber(hours)} ч`;
}

/** Сколько дней осталось до даты (0, если прошла). */
export function daysLeft(value) {
  const date = new Date(value);
  if (isNaN(date)) return 0;
  return Math.max(0, Math.ceil((date.getTime() - Date.now()) / 86_400_000));
}

// ──────────────────────────── Шапка сайта ────────────────────────────
// Структура совпадает с шапкой вики и лендинга: логотип, «JustKiwi / раздел ▾»,
// справа тема, профиль и колокольчик уведомлений.

/** Закрыть все выпадающие меню шапки, кроме указанного. */
function closeAllMenus(except) {
  document.querySelectorAll(".jk-menu").forEach((node) => {
    if (node !== except) node.classList.add("hidden");
  });
}
document.addEventListener("click", () => closeAllMenus());
document.addEventListener("keydown", (event) => { if (event.key === "Escape") closeAllMenus(); });

/** Привязать кнопку к выпадающему меню: открытие одного закрывает остальные. */
function bindMenu(trigger, menu) {
  menu.classList.add("jk-menu");
  trigger.setAttribute("aria-haspopup", "true");
  trigger.setAttribute("aria-expanded", "false");
  trigger.addEventListener("click", (event) => {
    event.stopPropagation();
    const wasHidden = menu.classList.contains("hidden");
    closeAllMenus();
    if (wasHidden) {
      menu.classList.remove("hidden");
      play(menu, { opacity: [0, 1], transform: ["translateY(-4px)", "none"] }, { duration: 0.14 });
    }
    trigger.setAttribute("aria-expanded", wasHidden ? "true" : "false");
  });
  menu.addEventListener("click", (event) => event.stopPropagation());
}

const MENU_CLASS = "absolute top-full mt-2 rounded-xl border border-line p-1 hidden z-50 shadow-soft";

/** Логотип и переключатель разделов сайта. current — «главная» | «вики» | «кабинет» | «профиль». */
function sectionSwitcher(current) {
  const sections = [["главная", "/"], ["вики", "/wiki/"], ["кабинет", "/cabinet/"]];
  const menu = el("div", { class: `${MENU_CLASS} left-0 w-40` },
    sections.filter(([key]) => key !== current).map(([key, href]) =>
      el("a", { href, class: "jk-menu-item capitalize" }, key)));
  const switchButton = el("button", {
    type: "button",
    class: "inline-flex items-center gap-1 whitespace-nowrap text-muted hover:text-ink transition",
  }, [`/ ${current}`, icon("chevron-down", 14)]);
  bindMenu(switchButton, menu);
  // На телефоне надпись скрыта, поэтому имя ссылки задаёт aria-label, а логотип декоративный.
  return el("div", { class: "flex items-center gap-2 min-w-0" }, [
    el("a", { href: "/", class: "flex items-center gap-2 font-extrabold text-lg shrink-0", "aria-label": "JustKiwi — на главную" }, [
      el("img", { src: "/files/just-96.webp", width: "28", height: "28", alt: "", class: "rounded-md" }),
      el("span", { class: "hidden sm:inline", "aria-hidden": "true" }, "JustKiwi"),
    ]),
    el("div", { class: "relative" }, [switchButton, menu]),
  ]);
}

const ICON_BUTTON = "relative w-10 h-10 inline-flex items-center justify-center rounded-xl border border-line bg-surface hover:bg-raised transition";

/** Переключатель темы (ключ jk_theme общий с вики). */
function themeToggle() {
  const dark = () => document.documentElement.classList.contains("dark");
  const button = el("button", { type: "button", class: ICON_BUTTON, title: "Сменить тему", "aria-label": "Сменить тему" },
    icon(dark() ? "sun" : "moon", 18));
  button.addEventListener("click", () => {
    const isDark = document.documentElement.classList.toggle("dark");
    try { localStorage.setItem("jk_theme", isDark ? "dark" : "light"); } catch (storageError) { /* приватный режим */ }
    button.replaceChildren(icon(isDark ? "sun" : "moon", 18));
  });
  return button;
}

/** Меню профиля: тег и ник; публичный профиль, кабинет, админ-панель (персоналу), выход. */
function profileMenu(current, session, profile) {
  const staff = Array.isArray(session.adminSections) && session.adminSections.length > 0;
  const items = [
    el("a", { href: `/u/${encodeURIComponent(session.name)}`, class: "jk-menu-item" }, [icon("user", 16), "Публичный профиль"]),
    current !== "кабинет" ? el("a", { href: "/cabinet/", class: "jk-menu-item" }, [icon("wallet", 16), "Личный кабинет"]) : null,
    staff && current !== "админка" ? el("a", { href: "/admin/", class: "jk-menu-item" }, [icon("shield-check", 16), "Админ-панель"]) : null,
    el("button", {
      type: "button",
      class: "jk-menu-item",
      title: "Сессия хранится в подписанной cookie, поэтому выход завершает вход на всех ваших устройствах",
      // После выхода страница перезагружается: кабинет покажет вход, профиль — гостевой вид.
      onclick: async () => {
        await api.post("/api/auth/logout");
        location.reload();
      },
    }, [icon("log-out", 16), "Выйти на всех устройствах"]),
  ];
  const menu = el("div", { class: `${MENU_CLASS} right-0 w-72` }, items);
  const button = el("button", {
    type: "button",
    class: "h-10 px-3 rounded-xl border border-line bg-surface hover:bg-raised transition flex items-center gap-2 min-w-0",
  }, [
    // На телефоне в шапке тесно: тег показываем с планшета, на узком экране — только значок.
    profile && profile.tag ? el("span", { class: "hidden sm:inline-flex" }, tagBadge(profile.tag, profile.tagColor)) : null,
    el("span", { class: "hidden sm:inline text-sm font-semibold text-kiwi truncate max-w-[140px]" }, session.name || ""),
    el("span", { class: "sm:hidden text-kiwi" }, icon("user", 18)),
  ]);
  bindMenu(button, menu);
  return el("div", { class: "relative" }, [button, menu]);
}

/**
 * Колокольчик уведомлений (например, ответы на сообщения об ошибках в вики) со счётчиком
 * непрочитанных. API отвечает { notifications: [{ id, message, is_read, created_at }], unreadCount }.
 * Нажатие на непрочитанное уведомление отмечает его прочитанным.
 */
function notifBell() {
  // Число на значке дублируется в aria-label кнопки, сам значок для диктора скрыт.
  const badge = el("span", {
    class: "absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-danger text-white text-[10px] font-bold items-center justify-center hidden",
    "aria-hidden": "true",
  });
  const panel = el("div", { class: `${MENU_CLASS} right-0 w-80 max-w-[calc(100vw-32px)] max-h-96 overflow-auto p-2` });
  const button = el("button", { type: "button", class: ICON_BUTTON, title: "Уведомления", "aria-label": "Уведомления" },
    [icon("bell", 18), badge]);

  function setUnread(count) {
    badge.textContent = String(count);
    badge.classList.toggle("hidden", count === 0);
    badge.classList.toggle("flex", count > 0);
    button.setAttribute("aria-label", count > 0 ? `Уведомления, новых: ${count}` : "Уведомления");
  }

  function notice(item) {
    const content = el("div", { class: "min-w-0" }, [
      el("p", { class: item.is_read ? "text-muted" : "" }, item.message),
      el("p", { class: "mt-1 text-xs text-muted" }, fmtDate(item.created_at)),
    ]);
    if (item.is_read) return el("div", { class: "jk-notice" }, content);
    const node = el("button", { type: "button", class: "jk-notice jk-notice--unread w-full text-left hover:bg-raised transition", title: "Отметить прочитанным" }, content);
    node.addEventListener("click", async () => {
      await api.post(`/api/notifications/${item.id}/read`);
      load();
    });
    return node;
  }

  function render(list) {
    panel.replaceChildren(
      el("p", { class: "px-2 pt-1 pb-2 text-sm font-semibold" }, "Уведомления"),
      ...(list.length ? list.slice(0, 8).map(notice) : [el("p", { class: "px-2 pb-2 text-sm text-muted" }, "Уведомлений пока нет")]),
      el("a", { href: "/wiki/notifications", class: "jk-menu-item mt-1 justify-center" }, "Все уведомления"),
    );
  }

  async function load() {
    const response = await api.get("/api/notifications");
    const data = response.ok && response.data ? response.data : {};
    setUnread(Number(data.unreadCount) || 0);
    render(Array.isArray(data.notifications) ? data.notifications : []);
  }

  bindMenu(button, panel);
  button.addEventListener("click", () => {
    if (!panel.classList.contains("hidden")) load();
  });
  load();
  return el("div", { class: "relative" }, [button, panel]);
}

/**
 * Шапка сайта. current — подпись раздела; session — ответ /api/auth/session;
 * profile — ответ /api/me (для тега) или null.
 * Необязательно: links — ссылки разделов страницы ({ label, href, current }), видны на
 * широком экране; tools — узлы перед переключателем темы (поиск вики); loginHref — куда
 * ведёт «Войти» (вики передаёт вход в кабинет с возвратом на ту же страницу).
 */
export function siteHeader({ current, session, profile, links = [], tools = [], loginHref = "/cabinet/" }) {
  const authenticated = session && session.authenticated;
  const nav = links.length
    ? el("nav", { class: "hidden lg:flex self-stretch items-center gap-6 ml-6", "aria-label": "Разделы" },
      links.map((link) => el("a", { href: link.href, class: "jk-navlink", "aria-current": link.current ? "page" : null }, link.label)))
    : null;
  return el("header", { class: "sticky top-0 z-40 border-b border-line bg-page/80 backdrop-blur" }, [
    el("div", { class: "max-w-7xl mx-auto px-4 lg:px-6 h-16 flex items-center gap-2 sm:gap-3" }, [
      sectionSwitcher(current),
      nav,
      el("div", { class: "flex-1" }),
      tools,
      themeToggle(),
      authenticated ? profileMenu(current, session, profile) : el("a", { href: loginHref, class: "jk-btn jk-btn--primary" }, "Войти"),
      authenticated ? notifBell() : null,
    ]),
  ]);
}
